/**
 * Token storage + refresh plumbing for the API client.
 *
 * Split out of api/index.js so the refresh logic is testable and so the rules
 * about *when* a user is actually signed out live in exactly one place.
 *
 * Why this exists: the previous version refreshed once per failed request with
 * no coordination, stored only the access token (so refresh-token rotation
 * would break it), and treated *any* error — including an offline blip or a
 * 500 — as "session over", wiping all of localStorage.
 */

export const ACCESS_KEY = 'access_token';
export const REFRESH_KEY = 'refresh_token';

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setTokens = ({ access, refresh }) => {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  // The backend rotates refresh tokens, so a refresh response carries a new one
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};

/** Clear auth only — never touch the guest cart or other app state. */
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

/**
 * A failed request means "sign out" only when the server actively rejected the
 * credentials. A network error, timeout, CORS failure or 5xx means the server
 * or connection is unhappy — the session is probably still fine.
 */
export const isAuthFailure = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

export const isNetworkError = (error) => !error?.response;

/* ------------------------------------------------------------------ *
 * Single-flight refresh                                              *
 * ------------------------------------------------------------------ */

let refreshPromise = null;

/**
 * Refresh the access token, coalescing concurrent callers onto one request.
 *
 * Without this, a page that fires five requests at once when the access token
 * has just expired sends five refreshes; with rotation enabled the later ones
 * present an already-rotated token and fail, killing a perfectly good session.
 *
 * @param {(refresh: string) => Promise<{data: {access: string, refresh?: string}}>} requestRefresh
 * @returns {Promise<string>} the new access token
 */
export const refreshAccessToken = (requestRefresh) => {
  if (refreshPromise) return refreshPromise;

  const refresh = getRefreshToken();
  if (!refresh) return Promise.reject(new Error('no_refresh_token'));

  refreshPromise = requestRefresh(refresh)
    .then(({ data }) => {
      setTokens({ access: data.access, refresh: data.refresh });
      return data.access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/** Test seam — drops any in-flight refresh. */
export const resetRefreshState = () => {
  refreshPromise = null;
};

/** Public routes a signed-out visitor is allowed to stay on. */
const PUBLIC_PATHS = [
  '/', '/products', '/second-hand', '/blog', '/cart', '/signin', '/signup',
];

export const isPublicPath = (pathname) =>
  PUBLIC_PATHS.some((p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`)));
